import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { displaySchemaApi } from '@rsos/reduxDataStore/src/features/displaySchema/displaySchemaApi';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  DYNAMIC_LAYERS_CARD,
  closeAllCards,
  setMapCardData,
  setCardsToRender,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { captureException } from '@rsos/utils/sentry';
import { inactivateAviveRequest } from '../../../actions/spatialLayers';
import {
  AVIVE2_STATUS_AED_CANCELED,
  AVIVE2_STATUS_AED_CONCLUDED,
  AVIVE2_STATUS_CANCELLATION_FAILED,
  AVIVE_STATUS_FAILED_TO_CONTACT,
  AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR,
  AVIVE_STATUS_FAILURE,
  AVIVE_STATUS_REQUEST,
  AVIVE_STATUS_SEARCHING,
  findAviveStatus,
  AVIVE2_STATUS_CANCELING,
  AVIVE2_STATUS_EMERGENCY_TIMEOUT,
  AVIVE_STATUS_CONTACT_TIMEOUT,
} from './aviveStatusTypes';
import {
  trackCanceled,
  trackNoteUpdated,
  trackOptionSearchByLocation,
  trackSearched,
  trackTimeout,
} from './aviveTrackings';
import { createPlaceholder } from './fixtures/avive-request-spatial';
import { postREMTrigger } from './postREMTrigger';

export class AviveFailedToContactError extends Error {
  constructor() {
    super(AVIVE_STATUS_FAILED_TO_CONTACT);
    this.name = 'AviveFailedToContactError';
  }
}

export class AviveContactTimeoutError extends Error {
  constructor() {
    super(AVIVE_STATUS_CONTACT_TIMEOUT);
    this.name = 'AviveContactTimeoutError';
  }
}

export class AviveFailureError extends Error {
  constructor() {
    super(AVIVE_STATUS_FAILURE);
    this.name = 'AviveFailureError';
  }
}
export class AviveDuplicateRequestError extends Error {
  constructor() {
    super(AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR);
    this.name = 'AviveDuplicateRequestError';
  }
}

export class AviveCallStartTimeError extends Error {
  constructor(value) {
    super(`The call_start_time is set to: type ${typeof value}, ${value}.`);
    this.name = 'AviveCallStartTimeError';
  }
}

// CallFlows that are needed for Avive to work.
const CALLFLOWS = {
  initiation: '__INTERNAL__/aviveInitiation',
};

/**
 * Normalizes the phone number to the format that Avive expects.
 * @param {string} phone
 */
export const normalizePhone = (phone = '') => {
  // Phone number should not have a leading `+`.
  const cleanPhone = `${phone}`.replace(/^\+/, '').trim();

  // If no valid phone was given, return an empty string.
  if (!cleanPhone) {
    return '';
  }

  // Phone number should have a leading `1` if it doesn't already.
  return cleanPhone.startsWith('1') ? cleanPhone : `1${cleanPhone}`;
};

/**
 * Open a MapCard with placeholder info for the given phone number.
 */
export const openAviveRequestMapCard = createAsyncThunk(
  'avive/openRequestMapCard',
  async (payload, { dispatch }) => {
    const placeholder = createPlaceholder({
      key: 'avive_aed_request',
      phone: payload?.phone,
      latitude: payload?.latitude,
      longitude: payload?.longitude,
      display: false,
      rgAddress: {},
    });

    const schema = placeholder?.body?.layer;

    schema.description = 'Source: Avive';
    schema.source = 'Avive';
    schema.attributes.propertiesOrder = ['phone_number'];

    // Open a MapCard with placeholder info until the actual request object
    // arrives which will be handled by handleAviveRequestObject.
    dispatch(
      setMapCardData({
        layerData: schema,
        spatialObject: {
          attributes: { phone_number: payload?.phone },
          shape: {
            features: [
              {
                geometry: {
                  coordinates: [payload?.longitude, payload?.latitude],
                },
              },
            ],
          },
        },
      }),
    );
    dispatch(setCardsToRender(DYNAMIC_LAYERS_CARD));
  },
);

/**
 * Re-open a MapCard for the given phone number.
 */
export const reopenAviveRequestMapCard = createAsyncThunk(
  'avive/reopenRequestMapCard',
  async (payload, { dispatch, getState }) => {
    const phone = normalizePhone(payload);
    const state = getState();
    const currentPhone = state?.avive?.currentPhone;

    if (phone === currentPhone) {
      const foundLayer = state.spatialLayers?.dynamicLayers?.find(layer => {
        return layer.key === 'avive_aed_request';
      });

      if (foundLayer) {
        const spatialObject = foundLayer?.spatial_objects?.find(obj => {
          return normalizePhone(obj?.attributes?.phone_number) === phone;
        });
        if (spatialObject) {
          const schema = spatialObject;

          dispatch(
            setMapCardData({
              layerData: foundLayer,
              spatialObject: {
                attributes: { phone_number: `+${phone}` },
                shape: schema?.shape,
              },
            }),
          );
          dispatch(setCardsToRender(DYNAMIC_LAYERS_CARD));
        }
      }
    }
  },
);

/**
 * Handle when a request spatial object is received.
 */
export const handleAviveRequestObject = createAsyncThunk(
  'avive/handleAviveRequestObject',
  async (payload, { dispatch, getState }) => {
    const phone = normalizePhone(payload?.attributes?.phone_number);
    const currentPhone = getState()?.avive?.currentPhone;

    // By this time, the MapCard is already open and displaying placeholder
    // information for the search. If the search was initiated by this user
    // then update the card with the request object that just arrived.
    // NOTE: The MapCard will automatically lookup the corresponding AED status.
    if (phone === currentPhone) {
      dispatch(
        setMapCardData({
          layerData: payload?.layer,
          spatialObject: payload,
        }),
      );
    }
  },
);

export const updateNotes = createAsyncThunk(
  'avive/updateNotes',
  async ({ phone, notes = '' }) => {
    try {
      const { payload } = await postREMTrigger(CALLFLOWS.initiation, {
        phone: `+${normalizePhone(phone)}`,
        notes,
        requestMethod: 'PATCH',
      });
      trackNoteUpdated();
      return payload;
    } catch (error) {
      throw new AviveFailureError();
    }
  },
);

export const cancelSearch = createAsyncThunk(
  'avive/cancelSearch',
  async ({ phone }) => {
    try {
      const { payload } = await postREMTrigger(CALLFLOWS.initiation, {
        phone: `+${normalizePhone(phone)}`,
        requestMethod: 'DELETE',
      });
      return payload;
    } catch (error) {
      throw new AviveFailureError();
    }
  },
);

/**
 * Search Avive AED using location coordinates.
 * @param {Object} params
 * @param {string} params.phone
 * @param {Object} params.location
 * @param {string} params.location.latitude
 * @param {string} params.location.longitude
 * @param {string} params.notes
 * @param {boolean} params.proxied - whether invoked by another action creator.
 */
export const searchByLocation = createAsyncThunk(
  'avive/searchLocation',
  async (
    { phone, location, notes = '', isStandaloneSearch = false },
    { getState },
  ) => {
    const globalState = getState();
    const callInfo = globalState?.emergencyCalls?.callQueue[phone]?.core;
    const allLocations = callInfo?.locations || [];
    const firstLocation = allLocations[0];
    const email_address = globalState?.sinatra?.user?.profile?.email;
    const agency_name = globalState?.psaps?.currentPSAP?.name;
    const agency_id = globalState?.psaps?.currentPSAP?.account_id;

    let call_start_time = parseInt(firstLocation?.call_start_time, 10);
    if (isStandaloneSearch) {
      // Since this is a standalone AED search, there's no default
      // call start time, set it to now.
      call_start_time = Date.now();
    }

    const reqBody = {
      phone: `+${normalizePhone(phone)}`,
      location: {
        latitude: parseFloat(location.latitude),
        longitude: parseFloat(location.longitude),
      },
      notes,
      call_start_time,
      email_address,
      agency_name,
      agency_id,
    };

    try {
      const { payload } = await postREMTrigger(CALLFLOWS.initiation, reqBody);
      trackOptionSearchByLocation();
      trackSearched();
      return payload;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new AviveDuplicateRequestError();
      }
      if (error.response?.status === 503) {
        throw new AviveFailedToContactError();
      }
      if (error.response?.status === 504) {
        throw new AviveContactTimeoutError();
      }
      throw new AviveFailureError();
    }
  },
);

const createInitialStatusStates = () => ({
  isNotesSent: false,
  isNotesError: false,
});

const initialState = {
  currentPhone: '',
  loading: false,
  searching: false,
  isNotesVisible: false,
  errors: {
    loading: null,
  },
  statuses: {},
};

/**
 * Takes a raw Avive AED hermes message, interprets it and saves it.
 * @param {Object} state - RTK state param
 * @param {Object} action - RTK action param
 * @param {Object} action.payload - The raw avive hermes message
 */
export const handleAviveRawMessage = createAsyncThunk(
  'avive/handleAviveRawMessage',
  async (payload, { dispatch, getState }) => {
    const state = getState().avive;

    if (!payload) {
      // If there's no payload, something went wrong with the message
      // itself coming from Hermes. Reset the state to the initial state
      // and return immediately.
      return { ...initialState };
    }

    const { raw = {} } = payload;
    const {
      topic,
      body: data = null,
      display_schema: displaySchema = null,
    } = raw;

    const createdTime = raw?.created_time || 0;

    const { emergency_status_update = '' } = data;
    const topicParts = topic.split('://');
    const phone = normalizePhone(topicParts.length > 0 ? topicParts[1] : '');

    const handleAviveStatusError = error => {
      captureException(error);
    };

    let states = state.statuses[topic]?.states;
    if (Object.keys(states || {}).length === 0) {
      states = createInitialStatusStates();
    }

    const isTimeout =
      emergency_status_update === AVIVE2_STATUS_EMERGENCY_TIMEOUT;

    const isConcluded = emergency_status_update === AVIVE2_STATUS_AED_CONCLUDED;

    const prevStates = state.statuses[topic]?.states || {};

    let isSearchActive = !!prevStates.isSearchActive;
    if (isTimeout || isConcluded) {
      isSearchActive = false;
      if (isTimeout) {
        trackTimeout();
      }
    }

    const isCanceled = emergency_status_update === AVIVE2_STATUS_AED_CANCELED;
    if (isTimeout || isConcluded || isCanceled) {
      // Notify to the spatialLayers.dynamicLayers slice that the AED request
      // should be marked as inactivated. There's no other way for the state
      // to propagated to the marker icon without this dispatched action.
      dispatch(inactivateAviveRequest(phone));
    }

    let fetchedDisplaySchema = null;
    if (!displaySchema) {
      try {
        const displaySchemaUrl = payload?.raw?.display_schema_url;
        const resp = await dispatch(
          displaySchemaApi.endpoints.getDisplaySchema.initiate(
            displaySchemaUrl,
          ),
        );
        if (resp?.data) {
          fetchedDisplaySchema = resp.data;
        }
      } catch {
        // in the rare case that the display schema fails to load, more likely
        // due to a user-space issue like connectivity, we'll just continue
        // rendering the status without the display schema and fail gracefully.
      }
    }

    return {
      ...state.statuses,
      [`tel://${phone}`]: {
        phone,
        data,
        status: isTimeout
          ? {}
          : findAviveStatus(emergency_status_update, handleAviveStatusError),
        displaySchema: fetchedDisplaySchema || displaySchema,
        createdTime,
        states: {
          ...prevStates,
          isTimeout,
          isSearchActive,
        },
      },
    };
  },
);

const aviveSlice = createSlice({
  name: 'aviveSlice',
  initialState,
  reducers: {
    restoreAviveNotes(state) {
      const phone = normalizePhone(state.currentPhone);
      const telKey = `tel://${phone}`;
      const prevStatuses = state.statuses[telKey] || {};
      const prevStates = prevStatuses.states || {};
      state.statuses = {
        ...state.statuses,
        [telKey]: {
          ...prevStatuses,
          states: {
            ...prevStates,
            isNotesSent: false,
            isNotesError: false,
            isTimeout: false,
          },
        },
      };
      state.isNotesVisible = true;
    },
    resetAviveNotes(state) {
      const phone = normalizePhone(state.currentPhone);
      const telKey = `tel://${phone}`;
      const prevStatuses = state.statuses[telKey] || {};
      const prevStates = prevStatuses.states || {};
      state.statuses = {
        ...state.statuses,
        [telKey]: {
          ...prevStatuses,
          states: {
            ...prevStates,
            isNotesSent: false,
            isNotesError: false,
            isTimeout: false,
          },
        },
      };
      state.isNotesVisible = false;
    },

    toggleNotesVisibility(state, action) {
      const phone = normalizePhone(action?.payload?.phone);
      state.isNotesVisible = true;
      state.currentPhone = phone;
    },

    /**
     * Updates the avive.status, which serves as a way for status messages
     * to be categorized within the UI. These statuses only exist in the UI
     * to make the design work and render with the correct colors. Not to be
     * confused with the actual Avive AED API statuses. See AED_STATUS.
     */
    updateAviveStatus(state, action) {
      const { payload } = action;
      if (!payload) {
        // If no payload is given, this means the status of the UI should be
        // in an inactive state. Set it here. This is used as a way to show/hide
        // status components relating to the Avive UI.
        state.status = initialState.status;
      }
      state.status = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(handleAviveRawMessage.fulfilled, (state, action) => {
        state.statuses = action.payload;
      })
      .addCase(updateNotes.pending, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        const telKey = `tel://${phone}`;
        const prevStatuses = state?.statuses[telKey];
        const prevStates = prevStatuses?.states || {};

        const newStatuses = {
          ...state.statuses,
          [telKey]: {
            ...prevStatuses,
            states: {
              ...prevStates,
              isNotesSending: true,
              isNotesSent: false,
              isNotesError: false,
            },
          },
        };
        return {
          ...state,
          statuses: newStatuses,
          isNotesVisible: true,
        };
      })
      .addCase(updateNotes.fulfilled, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        const telKey = `tel://${phone}`;
        const prevStatuses = state?.statuses[telKey] || {};
        const prevStates = prevStatuses?.states || {};

        const newState = {
          ...state,
          isNotesVisible: false,
          statuses: {
            ...state.statuses,
            [telKey]: {
              ...prevStatuses,
              states: {
                ...prevStates,
                isNotesSending: false,
                isNotesSent: true,
              },
            },
          },
        };
        return newState;
      })
      .addCase(updateNotes.rejected, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        const telKey = `tel://${phone}`;
        const prevStatuses = state?.statuses[telKey] || {};
        const prevStates = prevStatuses?.states || {};

        const newState = {
          ...state,
          isNotesVisible: true,
          statuses: {
            ...state.statuses,
            [telKey]: {
              ...prevStatuses,
              states: {
                ...prevStates,
                isNotesSending: false,
                isNotesSent: false,
                isNotesError: true,
              },
            },
          },
        };
        return newState;
      })
      .addCase(cancelSearch.pending, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        const states = state.statuses[`tel://${phone}`]?.states || {};

        const newStatuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            phone,
            data: null,
            status: findAviveStatus(AVIVE2_STATUS_CANCELING),
            states: {
              ...states,
              isSearchActive: false,
              isCanceling: true,
              isCanceled: false,
            },
          },
        };

        return {
          ...state,
          statuses: newStatuses,
        };
      })
      .addCase(cancelSearch.fulfilled, (state, action) => {
        trackCanceled();
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        const states = state.statuses[`tel://${phone}`]?.states || {};
        const newStatuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            phone,
            data: null,
            status: findAviveStatus(AVIVE2_STATUS_AED_CANCELED),
            states: {
              ...states,
              isSearchActive: false,
              isCanceling: false,
              isCanceled: true,
            },
          },
        };
        return {
          ...initialState,
          statuses: newStatuses,
        };
      })
      .addCase(cancelSearch.rejected, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');
        const newStatuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            phone,
            data: null,
            status: findAviveStatus(AVIVE2_STATUS_CANCELLATION_FAILED),
          },
        };
        return {
          ...state,
          statuses: newStatuses,
        };
      })
      .addCase(searchByLocation.pending, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        state.currentPhone = phone;
        state.searching = true;
        state.loading = true;
        state.statuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            phone,
            data: null,
            status: findAviveStatus(AVIVE_STATUS_REQUEST),
            states: createInitialStatusStates(),
          },
        };
      })
      .addCase(searchByLocation.fulfilled, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        state.currentPhone = phone;
        state.loading = false;

        const prevStatuses = state.statuses[`tel://${phone}`] || {};
        const states = prevStatuses.states || {};
        state.statuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            ...prevStatuses,
            phone,
            data: null,
            status: findAviveStatus(AVIVE_STATUS_SEARCHING),
            states: {
              ...states,
              isSearchActive: phone,
            },
          },
        };
      })
      .addCase(searchByLocation.rejected, (state, action) => {
        const { meta } = action;
        const phone = normalizePhone(meta?.arg?.phone || '');

        state.currentPhone = phone;
        state.loading = false;

        const prevStatuses = state.statuses[`tel://${phone}`] || {};
        const states = prevStatuses.states || {};
        state.statuses = {
          ...state.statuses,
          [`tel://${phone}`]: {
            ...prevStatuses,
            phone,
            data: null,
            status: findAviveStatus(action?.error?.message),
            states: {
              ...states,
              isSearchActive: false,
            },
          },
        };
        state.errors.loading = action;
      })
      .addCase(setSelectedCallerID, (state, action) => {
        const phone = normalizePhone(action?.payload);

        const newStatuses = {
          ...state.statuses,
        };

        const cleanableStates = [
          AVIVE2_STATUS_AED_CANCELED,
          AVIVE2_STATUS_AED_CONCLUDED,
        ];

        const targetStatus = state.statuses[`tel://${phone}`]?.status?.message;

        if (cleanableStates.includes(targetStatus)) {
          // TODO(Avive) Confirm that we can clear these out when call is deselected
        }

        state.currentPhone = phone;
        state.statuses = newStatuses;
        state.isNotesVisible = false;
      })
      .addCase(closeAllCards, state => {
        // This matches the `resetAviveNotes`
        const phone = normalizePhone(state.currentPhone);
        const telKey = `tel://${phone}`;
        const prevStatuses = state.statuses[telKey] || {};
        const prevStates = prevStatuses.states || {};
        state.statuses = {
          ...state.statuses,
          [telKey]: {
            ...prevStatuses,
            states: {
              ...prevStates,
              isNotesSent: false,
              isNotesError: false,
              isTimeout: false,
            },
          },
        };
        state.isNotesVisible = false;
      });
  },
});

export const {
  updateAviveStatus,
  toggleNotesVisibility,
  resetAviveNotes,
  restoreAviveNotes,
} = aviveSlice.actions;

export default aviveSlice.reducer;
